import React, { useLayoutEffect, useState } from 'react';

import { isAllowedLinkUrl, resolveHrefForNavigation } from '../../lib/noteHtml/linkSchemes';
import {
    useHoveredNoteLink,
    noteLinkPopoverEntered,
    noteLinkPopoverLeft,
} from './noteLinkHoverStore';
import './NoteLinkPreview.scss';

const VIEWPORT_MARGIN = 6;
const BELOW_ZONE_GAP = 6;

const truncateLink = (url = '', start = 45, end = 35) =>
    url.length > start + end
        ? `${url.slice(0, start)}…${url.slice(-end)}`
        : url;

// Clamps a popover of size (w, h) anchored at `position`'s bottom-left
// (position.y + BELOW_ZONE_GAP) into the viewport. A plain min/max clamp,
// not NoteLinkMenu's flip-above-when-no-room strategy (spec 8b just says
// "clamped to viewport") — measured against the node's OWN rendered size
// (unknown ahead of render, since the URL text length varies), so this runs
// in a layout effect after mount/whenever the content or anchor changes.
const clampToViewport = (position, rect) => {
    const popW = rect?.width ?? 0;
    const popH = rect?.height ?? 0;
    let top = position.y + BELOW_ZONE_GAP;
    top = Math.min(top, window.innerHeight - VIEWPORT_MARGIN - popH);
    top = Math.max(VIEWPORT_MARGIN, top);
    let left = position.x;
    left = Math.min(left, window.innerWidth - VIEWPORT_MARGIN - popW);
    left = Math.max(VIEWPORT_MARGIN, left);
    return { top, left };
};

// Shared link popover: URL-only (canvas hover, task A — this file's only
// current caller) or URL+actions (in-editor click menu, task B — not wired
// up yet, but the prop surface exists now so that later work only has to
// render this component with `actions` set, not invent a second one).
//
// Dual read path so ONE component serves both: with no `href`/`position`
// props (the canvas case — `<NoteLinkPreview />` in Diagram.jsx, no props
// at all) it subscribes to the canvas-driven pub/sub store itself; a future
// caller that already has its own href/position (e.g. a click handler with
// a DOM Range rect) can instead pass them straight in and this component
// won't touch the store. `actions`, when provided, renders custom buttons
// (Edit/Remove for task B) below the URL row.
const NoteLinkPreview = ({ href: hrefProp, position: positionProp, actions } = {}) => {
    const hovered = useHoveredNoteLink();
    const usingStore = hrefProp === undefined && positionProp === undefined;
    const href = usingStore ? hovered?.href ?? null : hrefProp;
    const position = usingStore
        ? (hovered ? { x: hovered.x, y: hovered.y } : null)
        : positionProp;
    const storeActions = usingStore ? hovered?.actions : undefined;
    const resolvedActions = actions !== undefined ? actions : storeActions;

    const [nodeRef, setNodeRef] = useState(null);
    const [style, setStyle] = useState(null);

    useLayoutEffect(() => {
        if (!nodeRef || !position) { setStyle(null); return; }
        const rect = nodeRef.getBoundingClientRect();
        const { top, left } = clampToViewport(position, rect);
        setStyle({ position: 'fixed', top: `${top}px`, left: `${left}px` });
        // href/actions can change the popover's rendered size (URL length,
        // presence of the actions row) without `position` itself moving —
        // re-measure whenever any of them change, not just on `position`.
    }, [nodeRef, position?.x, position?.y, href, resolvedActions]);

    if (!href || !position) return null;

    const isLink = isAllowedLinkUrl(href);

    return (
        <div
            ref={setNodeRef}
            className="note-link-preview"
            style={style || { position: 'fixed', top: -9999, left: -9999 }}
            onMouseEnter={noteLinkPopoverEntered}
            onMouseLeave={noteLinkPopoverLeft}
        >
            <div className="note-link-preview__url">
                {isLink
                    ? (
                        // <a href={href} target="_blank" rel="noopener noreferrer">
                        // title: native tooltip showing the FULL address on hover —
                        // both truncateLink() (JS, above start+end char count) and the
                        // stylesheet's own overflow:hidden/text-overflow:ellipsis
                        // (NoteLinkPreview.scss's `&__url`) can each cut this text off
                        // independently of the other, so the rendered text isn't a
                        // reliable read of the real href either way. A native `title`,
                        // not the app's Radix Tooltip primitive (ContextMenu.jsx's
                        // `Tooltip.Root`/`Trigger`/`Portal`/`Content`): this popover is
                        // ITSELF an on-hover floating box, already juggling its own
                        // pointer-enter/leave grace period (noteLinkPopoverEntered/
                        // noteLinkPopoverLeft, imported above) — nesting a second
                        // Radix tooltip trigger/portal inside it would fight that
                        // logic and its own show/hide timing for no benefit over the
                        // browser's own hover tooltip.
                        //
                        // href is resolveHrefForNavigation(href), NOT the raw stored
                        // value: a link stored before normalizeLinkUrl existed (or
                        // written by any path that bypasses it, e.g. a pasted
                        // `<a href="example.com">`) has no scheme, and
                        // isAllowedLinkUrl deliberately lets that through — an
                        // unresolved `href="example.com"` is a RELATIVE link, so a
                        // click would open "<app-origin>/example.com" instead of
                        // "https://example.com". Displayed text/title stay the raw
                        // stored `href` (truncateLink(href)/title={href} below) —
                        // only the navigation target is resolved.
                        //
                        // onMouseDown preventDefault — same reasoning/pattern as the
                        // Edit/Remove buttons below (EditableNote.jsx): an <a> is
                        // focusable, and on the in-editor click-popover surface this
                        // anchor sits in a portal OUTSIDE the note's contenteditable
                        // root, so an unguarded mousedown shifts focus to the anchor,
                        // blurring the editor and running its close-on-blur handler
                        // (handleEditorBlur -> closeLinkClickPopover) — which unmounts
                        // this very anchor before the click event that follows
                        // mousedown ever fires, silently swallowing the navigation.
                        // The anchor doesn't need focus to navigate on click, so
                        // suppressing the focus-shift costs nothing.
                        // onClick imperatively opens the resolved href via
                        // window.open() instead of trusting the anchor's own
                        // default click action. Root cause of a prior "click
                        // opens nothing" bug: ControlsLeft.jsx mounts a
                        // document-level 'click' listener (always present in
                        // Diagram.jsx) that called event.preventDefault()
                        // unconditionally, silently cancelling this anchor's
                        // default navigation with no error — see that file's
                        // handleDocumentClick for the actual fix. This
                        // handler is the DEFENSIVE layer on top of that fix:
                        // navigating imperatively from the element's own
                        // click handler survives ANY later listener on the
                        // same event calling preventDefault(), since
                        // window.open() doesn't depend on the browser's
                        // default action at all — only a full unmount of
                        // this anchor between mousedown and click (ruled out
                        // separately; see noteLinkHoverStore.js's hide-paths,
                        // none of which fire on a click originating here)
                        // would still defeat it. preventDefault() here stops
                        // the native default action from ALSO firing and
                        // opening a second duplicate tab.
                        <a
                            href={resolveHrefForNavigation(href)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={href}
                            onMouseDown={(event) => event.preventDefault()}
                            // onClick={(event) => {
                            //     event.preventDefault();
                            //     window.open(resolveHrefForNavigation(href), '_blank', 'noopener,noreferrer');
                            // }}
                        >
                            {truncateLink(href)}
                        </a>
                    )
                    : (
                        // Not JS-truncated (no truncateLink() call), but still subject
                        // to the same CSS ellipsis as the link case above when the raw
                        // text overflows — same native-title reasoning applies.
                        <span className="note-link-preview__url--plain" title={href}>{href}</span>
                    )}
            </div>
            {resolvedActions && (
                <div className="note-link-preview__actions">
                    {resolvedActions}
                </div>
            )}
        </div>
    );
};

export default NoteLinkPreview;
